if (!customElements.get('sticky-atc-bar')) {
  customElements.define(
    'sticky-atc-bar',
    class StickyAtcBar extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.mainSubmitButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        this.stickySubmitButton = this.querySelector('[data-sticky-submit]');
        this.selectionText = this.querySelector('[data-sticky-selection-text]');
        this.selectionContainer = this.querySelector('[data-sticky-selection]');
        this.editButton = this.querySelector('[data-sticky-edit]');
        this.stickyImage = this.querySelector('[data-sticky-image]');
        this.stickyPrice = this.querySelector('[data-sticky-price]');
        this.onSellingPlanChange = this.handleSellingPlanChange.bind(this);

        if (!this.mainSubmitButton || !this.stickySubmitButton) {
          this.remove();
          return;
        }

        this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.mobileMediaQuery = window.matchMedia('(max-width: 989px)');
        this.isMainAtcVisible = false;
        this.onViewportChange = this.handleViewportChange.bind(this);

        if (this.getEditTarget()) {
          this.editButton?.addEventListener('click', () => this.scrollToEditTarget());
        } else {
          this.editButton?.remove();
          if (!this.selectionText?.textContent?.trim()) {
            this.selectionContainer?.setAttribute('hidden', '');
          }
        }

        if (this.dataset.trigger === 'scroll') {
          this.initScrollTrigger();
        } else {
          this.initIntersectionObserver();
        }
        this.syncSubmitButton();
        this.updateSelectionSummary();
        this.bindSellingPlanListener();
        this.syncPrice();

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data?.sectionId !== this.sectionId) return;
          this.syncSubmitButton();
          if (event.data.variant) {
            this.updateSelectionSummaryFromVariant(event.data.variant);
            this.updateImageFromVariant(event.data.variant);
          }
          this.bindSellingPlanListener();
          this.syncPrice(event);
        });

        this.mobileMediaQuery.addEventListener('change', this.onViewportChange);

        const productForm = document.getElementById(this.dataset.productFormId)?.closest('product-form');
        if (productForm) {
          this.submitObserver = new MutationObserver(() => this.syncSubmitButton());
          this.submitObserver.observe(this.mainSubmitButton, {
            attributes: true,
            attributeFilter: ['class', 'disabled', 'aria-disabled'],
            subtree: true,
            childList: true,
          });
        }
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
        this.intersectionObserver?.disconnect();
        this.submitObserver?.disconnect();
        this.sellingPlanWidget?.removeEventListener('change', this.onSellingPlanChange);
        this.mobileMediaQuery?.removeEventListener('change', this.onViewportChange);
        if (this.onScroll) {
          window.removeEventListener('scroll', this.onScroll);
        }
        this.setBarVisible(false);
      }

      isVisibleForViewport() {
        const visibility = this.dataset.visibility || 'both';
        const isMobile = this.mobileMediaQuery.matches;

        if (visibility === 'mobile_only') return isMobile;
        if (visibility === 'desktop_only') return !isMobile;
        return true;
      }

      handleViewportChange() {
        this.setBarVisible(!this.isMainAtcVisible);
      }

      bindSellingPlanListener() {
        const widget = document.getElementById(`selling-plan-widget-${this.sectionId}`);
        if (widget === this.sellingPlanWidget) return;

        this.sellingPlanWidget?.removeEventListener('change', this.onSellingPlanChange);
        this.sellingPlanWidget = widget;

        if (!widget) return;

        widget.addEventListener('change', this.onSellingPlanChange);
      }

      handleSellingPlanChange() {
        this.updateSelectionSummaryFromSellingPlan();
        this.syncPriceFromSellingPlan();
        this.syncSubmitButton();
      }

      syncPrice(event) {
        if (document.getElementById(`selling-plan-widget-${this.sectionId}`)) {
          this.syncPriceFromSellingPlan();
          return;
        }

        if (event?.data?.html) {
          const source = event.data.html.getElementById(`price-${this.sectionId}`);
          if (source && this.stickyPrice) {
            this.stickyPrice.classList.remove('sticky-atc-bar__price--plan');
            this.stickyPrice.innerHTML = source.innerHTML;
            return;
          }
        }

        this.syncPriceFromMain();
      }

      syncPriceFromMain() {
        const mainPrice = document.getElementById(`price-${this.sectionId}`);
        if (!mainPrice || !this.stickyPrice) return;

        this.stickyPrice.classList.remove('sticky-atc-bar__price--plan');
        this.stickyPrice.innerHTML = mainPrice.innerHTML;
      }

      syncPriceFromSellingPlan() {
        if (!this.stickyPrice) return;

        const widget = document.getElementById(`selling-plan-widget-${this.sectionId}`);
        const checked = widget?.querySelector('input[name="selling-plan"]:checked');
        const planPrices = checked?.closest('label')?.querySelector('.plan-related-prices-container');

        if (!planPrices) {
          this.syncPriceFromMain();
          return;
        }

        this.stickyPrice.classList.add('sticky-atc-bar__price--plan');
        this.stickyPrice.innerHTML = planPrices.outerHTML;
      }

      getEditTarget() {
        return (
          document.getElementById(`variant-selects-${this.sectionId}`) ||
          document.getElementById(`selling-plan-widget-${this.sectionId}`)
        );
      }

      updateSelectionSummary() {
        const variantSelects = document.getElementById(`variant-selects-${this.sectionId}`);
        if (variantSelects) {
          const values = [];
          variantSelects.querySelectorAll('[data-selected-value]').forEach((element) => {
            const value = element.textContent.trim();
            if (value) values.push(value);
          });

          if (!values.length) {
            variantSelects.querySelectorAll('fieldset input:checked').forEach((input) => {
              values.push(input.value);
            });
          }

          if (!values.length) {
            variantSelects.querySelectorAll('select').forEach((select) => {
              if (select.value) values.push(select.value);
            });
          }

          if (values.length) {
            this.setSelectionText(values.join(' / '));
            return;
          }
        }

        this.updateSelectionSummaryFromSellingPlan();
      }

      updateSelectionSummaryFromVariant(variant) {
        if (!this.selectionText || !variant) return;

        if (variant.options?.length) {
          this.setSelectionText(variant.options.join(' / '));
        } else if (variant.title) {
          this.setSelectionText(variant.title);
        }
      }

      setSelectionText(value) {
        if (!this.selectionText) return;
        const trimmed = value?.trim?.() || '';
        this.selectionText.textContent = trimmed;
        this.selectionText.toggleAttribute('hidden', !trimmed);
      }

      updateImageFromVariant(variant) {
        if (!this.stickyImage || !variant) return;

        const variantSrc =
          variant.featured_media?.preview_image?.src || variant.featured_image?.src;

        if (variantSrc) {
          this.stickyImage.src = variantSrc;
          return;
        }

        if (this.stickyImage.dataset.fallbackSrc) {
          this.stickyImage.src = this.stickyImage.dataset.fallbackSrc;
        }
      }

      updateSelectionSummaryFromSellingPlan() {
        if (!this.selectionText) return;

        const widget = document.getElementById(`selling-plan-widget-${this.sectionId}`);
        if (!widget) return;

        const checked = widget.querySelector('input[name="selling-plan"]:checked');
        if (!checked) return;

        const heading = checked.closest('label')?.querySelector('.selling-plan-heading');
        if (heading?.textContent?.trim()) {
          this.setSelectionText(heading.textContent);
          return;
        }

        if (checked.dataset.subscription) {
          this.setSelectionText(checked.dataset.subscription);
        }
      }

      scrollToEditTarget() {
        const target = this.getEditTarget();
        if (!target) return;

        target.scrollIntoView({
          behavior: this.prefersReducedMotion ? 'auto' : 'smooth',
          block: 'center',
        });

        const focusable = target.querySelector(
          'fieldset input:not([disabled]), select:not([disabled]), .selling-plan-radio:not([disabled])'
        );
        focusable?.focus({ preventScroll: true });
      }

      syncSubmitButton() {
        const main = this.mainSubmitButton;
        const sticky = this.stickySubmitButton;
        const mainText = main.querySelector('span');
        const stickyText = sticky.querySelector('span');

        sticky.toggleAttribute('disabled', main.hasAttribute('disabled'));
        if (main.hasAttribute('aria-disabled')) {
          sticky.setAttribute('aria-disabled', main.getAttribute('aria-disabled'));
        } else {
          sticky.removeAttribute('aria-disabled');
        }

        if (mainText && stickyText) {
          stickyText.textContent = mainText.textContent;
        }

        const isLoading = main.classList.contains('loading');
        sticky.classList.toggle('loading', isLoading);
        sticky.toggleAttribute('aria-busy', isLoading);
      }

      initIntersectionObserver() {
        this.intersectionObserver = new IntersectionObserver(
          (entries) => {
            this.isMainAtcVisible = entries[0].isIntersecting;
            this.setBarVisible(!entries[0].isIntersecting);
          },
          { threshold: 0 }
        );
        this.intersectionObserver.observe(this.mainSubmitButton);
      }

      initScrollTrigger() {
        const offset = parseInt(this.dataset.scrollOffset || '1', 10);
        this.onScroll = () => {
          const scrolledPast = window.scrollY > offset;
          this.isMainAtcVisible = !scrolledPast;
          this.setBarVisible(scrolledPast);
        };
        this.onScroll();
        window.addEventListener('scroll', this.onScroll, { passive: true });
      }

      setBarVisible(visible) {
        visible = visible && this.isVisibleForViewport();

        this.toggleAttribute('hidden', !visible);
        this.setAttribute('aria-hidden', String(!visible));
        this.classList.toggle('tw-translate-y-0', visible);
        this.classList.toggle('tw-opacity-100', visible);
        this.classList.toggle('tw-pointer-events-auto', visible);
        this.classList.toggle('tw-translate-y-full', !visible);
        this.classList.toggle('tw-opacity-0', !visible);
        this.classList.toggle('tw-pointer-events-none', !visible);
        document.body.classList.toggle('sticky-atc-visible', visible);

        if (visible) {
          const height = this.getBoundingClientRect().height;
          document.documentElement.style.setProperty('--sticky-atc-height', `${height}px`);
        } else {
          document.documentElement.style.setProperty('--sticky-atc-height', '0px');
        }
      }
    }
  );
}
